import { ref, reactive, computed, watch } from 'vue'
import type { Filiere, FiliereRequest, FiliereFormData } from '~/types/administration'
import type { ApiResponse } from '~/types/api'
import { useApi } from './useApi'
import { useMessage } from './useMessage'
import { toast } from 'vue3-toastify'

interface FiliereState {
    filieres: Filiere[]
    filiere: Filiere | null
    loading: boolean
    errors: Record<string, string[]>
    filters: {
        search: string
        status: 'Tous' | 'Actifs' | 'Inactifs'
        parcours: string | null
        page: number
        per_page: number
    }
    pagination: {
        total: number
        current_page: number
        per_page: number
    }
}

export function useFiliere() {
    const { apiData } = useApi()
    const { addMessage } = useMessage()

    const state = reactive<FiliereState>({
        filieres: [],
        filiere: null,
        loading: false,
        errors: {},
        filters: {
            search: '',
            status: 'Tous',
            parcours: null,
            page: 1,
            per_page: 50
        },
        pagination: {
            total: 0,
            current_page: 1,
            per_page: 50
        }
    })

    const formRef = ref()

    const headers = [
        { title: 'Code', align: 'start' as const, key: 'code', sortable: true },
        { title: 'Nom', align: 'start' as const, key: 'nom', sortable: true },
        { title: 'Parcours', align: 'start' as const, key: 'parcours.niveau', sortable: true },
        { title: 'Statut', align: 'start' as const, key: 'is_active', sortable: true },
        { title: 'Actions', align: 'center' as const, key: 'actions', sortable: false }
    ]

    const rules = {
        nom: [
            (v: string) => !!v || 'Le nom est requis',
            (v: string) => v.length >= 2 || 'Le nom doit contenir au moins 2 caractères',
            (v: string) => v.length <= 255 || 'Le nom ne doit pas dépasser 255 caractères'
        ],
        parcours_id: [(v: number) => !!v || 'Le parcours est requis'],
        description: [(v: string) => !v || v.length <= 1000 || 'La description ne doit pas dépasser 1000 caractères'],
        domaine: [(v: string) => !v || v.length <= 255 || 'Le domaine ne doit pas dépasser 255 caractères'],
        mention: [(v: string) => !v || v.length <= 255 || 'La mention ne doit pas dépasser 255 caractères'],
        specialite: [(v: string) => !v || v.length <= 255 || 'La spécialité ne doit pas dépasser 255 caractères']
    }

    const loadingState = ref(false)
    let searchTimeout: NodeJS.Timeout | null = null

    const filteredFilieres = computed(() => {
        return state.filieres.filter(filiere => {
            const matchSearch = !state.filters.search || 
                filiere.nom.toLowerCase().includes(state.filters.search.toLowerCase()) ||
                filiere.code.toLowerCase().includes(state.filters.search.toLowerCase());
            
            const matchStatus = state.filters.status === 'Tous' || 
                (state.filters.status === 'Actifs' && filiere.is_active) ||
                (state.filters.status === 'Inactifs' && !filiere.is_active);
            
            const matchParcours = !state.filters.parcours ||
                filiere.parcours?.niveau === state.filters.parcours;
            
            return matchSearch && matchStatus && matchParcours;
        });
    });

    async function fetchFilieres() {
        try {
            loadingState.value = true;
            const params = new URLSearchParams();
            if (state.filters.search) params.append('search', state.filters.search);
            if (state.filters.status !== 'Tous') params.append('is_active', (state.filters.status === 'Actifs').toString());
            if (state.filters.parcours) params.append('niveau', state.filters.parcours);
            // Charger tout pour pagination client
            params.append('per_page', 'all')

            const response = await apiData<ApiResponse<any>>(`/filieres?${params.toString()}`);
            
            if (response?.success) {
                if (Array.isArray(response.data)) {
                    state.filieres = response.data
                    state.pagination = { total: response.data.length, current_page: 1, per_page: state.filters.per_page }
                } else if (response.data && typeof response.data === 'object' && 'data' in response.data) {
                    const list = (response.data as any).data || []
                    state.filieres = list
                    state.pagination = { total: list.length, current_page: 1, per_page: state.filters.per_page }
                } else {
                    state.filieres = []
                    state.pagination = { total: 0, current_page: 1, per_page: state.filters.per_page }
                }
            } else {
                state.filieres = []
                state.pagination = { total: 0, current_page: 1, per_page: state.filters.per_page }
            }
        } catch (error) {
            //console.error('Error in fetchFilieres:', error);
            state.filieres = [];
            state.pagination = { total: 0, current_page: 1, per_page: state.filters.per_page }
        } finally {
            loadingState.value = false;
        }
    }

    // Watchers ciblés + debounce
    watch(
        () => state.filters.search,
        () => {
            if (searchTimeout) clearTimeout(searchTimeout)
            searchTimeout = setTimeout(() => {
                state.filters.page = 1
                fetchFilieres()
            }, 300)
        }
    )
    watch(
        () => state.filters.status,
        () => {
            state.filters.page = 1
            fetchFilieres()
        }
    )
    watch(
        () => state.filters.parcours,
        () => {
            state.filters.page = 1
            fetchFilieres()
        }
    )

    async function fetchFiliere(id: number) {
        try {
            state.loading = true
            const response = await apiData<ApiResponse<Filiere>>(`/filieres/${id}`)
            
            if (response.success) {
                state.filiere = response.data
                return response.data
            } else {
                addMessage({
                    color: 'error',
                    text: response.message || 'Erreur lors de la récupération de la filière'
                })
                return null
            }
        } catch (error) {
            addMessage({
                color: 'error',
                text: "Erreur lors de la récupération de la filière"
            })
            return null
        } finally {
            state.loading = false
        }
    }

    // Fonction utilitaire pour supprimer les accents
    function removeAccents(str: string): string {
        return str.normalize('NFD').replace(/\p{Diacritic}/gu, '');
    }

    // Fonction pour générer le code à partir du nom (logique backend)
    function generateCodeFromName(nom: string): string {
        if (!nom) return '';
        let clean = removeAccents(nom.trim().toUpperCase());
        const words = clean.split(/\s+/);
        const stopWords = ['DE', 'DU', 'DES', 'LE', 'LA', 'LES', 'ET', 'OF', 'THE', 'AND', 'OR', 'FOR', 'IN', 'ON', 'AT', 'TO', 'WITH', 'BY', 'EN', 'DANS', 'SUR', 'POUR'];
        const filtered = words.filter((w: string) => w && !stopWords.includes(w));
        if (filtered.length === 1) {
            return filtered[0].substring(0, 4);
        } else if (filtered.length > 1) {
            let code = '';
            for (const word of filtered) {
                code += word[0];
            }
            return code.substring(0, 6);
        }
        return '';
    }

    const filiereForm = ref<FiliereFormData>({
        nom: '',
        description: '',
        domaine: '',
        mention: '',
        specialite: '',
        parcours_id: null as unknown as number,
        is_active: true,
        code_auto_generated: true,
        code: ''
    });

    function openDialog(type: 'create' | 'edit' = 'create', item?: Filiere) {
        if (type === 'edit' && item) {
            filiereForm.value = {
                id: item.id,
                nom: item.nom,
                description: item.description,
                domaine: item.domaine,
                mention: item.mention,
                specialite: item.specialite,
                parcours_id: item.parcours_id,
                is_active: item.is_active,
                // En édition, toujours désactiver la génération auto pour permettre les modifications
                code_auto_generated: false,
                code: item.code
            };
        } else {
            filiereForm.value = {
                nom: '',
                description: '',
                domaine: '',
                mention: '',
                specialite: '',
                parcours_id: null as unknown as number,
                is_active: true,
                code_auto_generated: true,
                code: ''
            };
        }
        state.errors = {};
    }

    watch(
        () => filiereForm.value.nom,
        (newNom) => {
            if (filiereForm.value.code_auto_generated) {
                filiereForm.value.code = generateCodeFromName(newNom || '');
            }
        }
    );

    watch(
        () => filiereForm.value.code_auto_generated,
        (isAuto) => {
            if (isAuto) {
                filiereForm.value.code = generateCodeFromName(filiereForm.value.nom || '');
            }
        }
    );

    watch(
        () => filiereForm.value.code,
        () => {
            if (state.errors.code) delete state.errors.code;
        }
    );

    watch(
        () => filiereForm.value.code_auto_generated,
        () => {
            if (state.errors.code) delete state.errors.code;
        }
    );

    async function createFiliere(data: FiliereFormData) {
        try {
            const payload = {
                nom: data.nom,
                description: data.description,
                domaine: data.domaine,
                mention: data.mention,
                specialite: data.specialite,
                parcours_id: data.parcours_id,
                is_active: data.is_active,
                code: data.code_auto_generated ? generateCodeFromName(data.nom || '') : (data.code || '')
            };
            const response = await apiData<ApiResponse<Filiere>>('/filieres', {
                method: 'POST',
                body: JSON.stringify(payload)
            })
            if (response.success) {
                await fetchFilieres()
                return { success: true, message: 'Filière créée avec succès' }
            }
            return { 
                success: false, 
                message: response.message || "Une erreur s'est produite lors de la création",
                errors: response.errors || {}
            }
        } catch (error: any) {
            const errorMessage = error?.response?._data?.message || error?.data?.message || "Une erreur s'est produite"
            
            // Détecter l'erreur de code en double
            const isDuplicateCodeError = errorMessage.toLowerCase().includes('code existe déjà') || 
                                         errorMessage.toLowerCase().includes('code already exists')
            
            return { 
                success: false, 
                message: errorMessage, 
                errors: error?.response?._data?.data || {},
                isDuplicateCodeError
            }
        }
    }

    async function updateFiliere(id: number, data: FiliereFormData) {
        try {
            const payload = {
                nom: data.nom,
                description: data.description,
                domaine: data.domaine,
                mention: data.mention,
                specialite: data.specialite,
                parcours_id: data.parcours_id,
                is_active: data.is_active,
                code: data.code_auto_generated ? generateCodeFromName(data.nom || '') : (data.code || '')
            };
            const response = await apiData<ApiResponse<Filiere>>(`/filieres/${id}`, {
                method: 'PUT',
                body: JSON.stringify(payload)
            })
            if (response.success) {
                await fetchFilieres()
                return { success: true, message: 'Filière mise à jour avec succès' }
            }
            return { 
                success: false, 
                message: response.message || "Une erreur s'est produite lors de la mise à jour",
                errors: response.errors || {}
            }
        } catch (error: any) {
            const errorMessage = error?.response?._data?.message || error?.data?.message || "Une erreur s'est produite"
            
            // Détecter l'erreur de code en double
            const isDuplicateCodeError = errorMessage.toLowerCase().includes('code existe déjà') || 
                                         errorMessage.toLowerCase().includes('code already exists')
            
            return { 
                success: false, 
                message: errorMessage, 
                errors: error?.response?._data?.data || {},
                isDuplicateCodeError
            }
        }
    }

    async function deleteFiliere(id: number) {
        try {
            const response = await apiData<ApiResponse<null>>(`/filieres/${id}`, {
                method: 'DELETE'
            })
            if (response.success) {
                await fetchFilieres()
                return { success: true, message: 'Filière supprimée avec succès' }
            }
            return { 
                success: false, 
                message: response.message || "Une erreur s'est produite lors de la suppression" 
            }
        } catch (error: any) {
            if (error?.response?._data?.data?.message?.includes('classes actives')) {
                return {
                    success: false,
                    message: "Impossible de supprimer cette filière car elle a des classes actives"
                }
            }
            throw error
        }
    }

    async function toggleActive(id: number) {
        try {
            const response = await apiData<ApiResponse<Filiere>>(`/filieres/${id}/toggle-active`, {
                method: 'PATCH'
            })
            
            if (response.success) {
                await fetchFilieres()
                return { success: true, message: response.message || 'Statut de la filière modifié avec succès' }
            }
            return { success: false, message: response.message || "Erreur lors de la modification du statut" }
        } catch (error) {
            //console.error('Error in toggleActive:', error)
            return { success: false, message: "Erreur lors de la modification du statut" }
        }
    }

    function resetForm() {
        if (formRef.value) {
            formRef.value.reset()
        }
        state.errors = {}
    }

    async function submitForm(data: FiliereFormData, id?: number) {
        const { valid } = await formRef.value?.validate();
        if (!valid) return { success: false };

        errorsClear();
        loadingState.value = true;

        try {
            const success = id
                ? await updateFiliere(id, data)
                : await createFiliere(data);

            loadingState.value = false;
            if (success) {
                return { success: true };
            }

            return { success: false, message: "Une erreur s'est produite" };
        } catch (error: any) {
            loadingState.value = false;
            const response = error?.response?._data;
            if (response?.message)
                return { success: false, message: response.message };

            Object.assign(state.errors, response);
            await formRef.value?.validate();
            return { success: false };
        }
    }

    function errorsClear() {
        state.errors = {};
    }

    return {
        filieres: filteredFilieres,
        filiere: computed(() => state.filiere),
        filiereForm,
        loading: loadingState,
        errors: state.errors,
        filters: state.filters,
        pagination: state.pagination,
        formRef,
        headers,
        rules,
        fetchFilieres,
        fetchFiliere,
        createFiliere,
        updateFiliere,
        deleteFiliere,
        toggleActive,
        resetForm,
        submitForm,
        generateCodeFromName,
        openDialog
    }
} 